<script setup>
import { computed, ref } from 'vue'
import { defaultVTooltipProps } from '@/utils/defaultProps'

const props = defineProps({
  controller: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  buttonTitle: {
    type: String,
    default: 'mdi-tune-variant',
  },
  title: {
    type: String,
    default: 'Display settings',
  },
  maxWidth: {
    type: [String, Number],
    default: '420',
  },
})

const dialog = ref(false)
const baseId = ref(`stdizer-appearance-settings-${Math.random().toString(36).slice(2, 8)}`)

const activatorButtonId = computed(() => `${baseId.value}-button`)

const settingsChanged = computed(
  () =>
    props.controller.recordIdField !== 'smiles' ||
    !!props.controller.hidden ||
    !!props.controller.components ||
    !!props.controller.changed ||
    !!props.controller.failed ||
    !!props.controller.passed ||
    !!props.controller.showDetails,
)

const hiddenModel = computed({
  get: () => !!props.controller.hidden,
  set: (value) => {
    props.controller.hidden = value
  },
})

const componentsModel = computed({
  get: () => !!props.controller.components,
  set: (value) => {
    props.controller.components = value
  },
})

const changedModel = computed({
  get: () => !!props.controller.changed,
  set: (value) => {
    props.controller.changed = value
  },
})

const failedModel = computed({
  get: () => !!props.controller.failed,
  set: (value) => {
    props.controller.failed = value
  },
})

const passedModel = computed({
  get: () => !!props.controller.passed,
  set: (value) => {
    props.controller.passed = value
  },
})

const showDetailsModel = computed({
  get: () => !!props.controller.showDetails,
  set: (value) => {
    props.controller.showDetails = value
  },
})

const recordIdFieldModel = computed({
  get: () => props.controller.recordIdField,
  set: (value) => {
    props.controller.recordIdField = value
  },
})
</script>

<template>
  <v-menu
    v-model="dialog"
    offset-y
    :close-on-content-click="false"
    :max-width="maxWidth"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        :id="activatorButtonId"
        class="border flat-button"
        v-bind="attrs"
        v-on="on"
        :disabled="disabled"
        :aria-label="title"
      >
        <v-badge
          v-if="settingsChanged"
          dot
          color="primary"
          offset-x="-4"
          offset-y="0"
        >
          <v-icon>
            {{ buttonTitle }}
          </v-icon>
        </v-badge>

        <v-icon v-else>
          {{ buttonTitle }}
        </v-icon>
      </v-btn>

      <v-tooltip
        top
        v-bind="defaultVTooltipProps"
        :activator="`#${activatorButtonId}`"
      >
        {{ title }}
      </v-tooltip>
    </template>

    <v-card class="settings-menu-card">
      <v-card-title>{{ title }}</v-card-title>

      <v-card-text class="settings-menu-card__content">
        <div class="d-flex flex-column gap-4">
          <div class="d-flex flex-column gap-2">
            <div class="font-weight-bold">Record display</div>

            <b-form-select
              v-model="recordIdFieldModel"
              :options="controller.recordIdOptions"
              :disabled="disabled"
              title="Record ID field"
            />
          </div>

          <div class="d-flex flex-column gap-1">
            <div class="font-weight-bold">Filters</div>

            <v-switch
              v-if="controller.countHidden"
              v-model="hiddenModel"
              :disabled="disabled"
              :label="`Show hidden records (${controller.countHidden})`"
              hide-details
              class="mt-0"
            />

            <v-switch
              v-if="controller.countComponents"
              v-model="componentsModel"
              :disabled="disabled"
              :label="`Show component records (${controller.countComponents})`"
              hide-details
              class="mt-0"
            />

            <v-switch
              v-if="controller.countChanged"
              v-model="changedModel"
              :disabled="disabled"
              :label="`Show changed records (${controller.countChanged})`"
              hide-details
              class="mt-0"
            />

            <v-switch
              v-if="controller.countFailed"
              v-model="failedModel"
              :disabled="disabled"
              :label="`Show failed records (${controller.countFailed})`"
              hide-details
              class="mt-0"
            />

            <v-switch
              v-if="controller.countPassed"
              v-model="passedModel"
              :disabled="disabled"
              :label="`Show passed records (${controller.countPassed})`"
              hide-details
              class="mt-0"
            />
          </div>

          <div class="d-flex flex-column gap-1">
            <div class="font-weight-bold">Appearance</div>

            <v-switch
              v-model="showDetailsModel"
              :disabled="disabled"
              label="Show additional details"
              hide-details
              class="mt-0"
            />
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn @click="dialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style scoped>
.settings-menu-card {
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.settings-menu-card__content {
  overflow-y: auto;
}
</style>
